import { HugeiconsIcon } from "@hugeicons/react";
import {
  Mail02Icon,
  UserEdit01Icon,
  PropertyEditIcon
} from "@hugeicons-pro/core-solid-rounded";
import { Button, ButtonVariant, Input, Radio, Show } from "shared/components";
import { Card } from "shared/components/Card";
import { SubmissionType } from "shared/types";
import { SubmissionDisclosureConfiguration } from "./types";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";
import { cx } from "shared/utils";

type SelectMethodStepProps = {
  configuration: SubmissionDisclosureConfiguration;
  setConfiguration: React.Dispatch<
    React.SetStateAction<SubmissionDisclosureConfiguration>
  >;
  onNext: () => void;
  isLoading: boolean;
};

export const SelectMethodStep = ({
  configuration,
  setConfiguration,
  onNext,
  isLoading
}: SelectMethodStepProps) => {
  const { partner } = useAuthContext();
  return (
    <>
      <div className="flex-1 pt-6 pb-4 space-y-4 overflow-y-auto">
        <Card className="max-w-[500px] mx-auto divide-y-[1px] divide-alpha-40">
          <Radio
            id="submission-type-by-email"
            name="submission-type"
            className="p-4 gap-4"
            checkboxClassName="my-auto"
            labelClassName="mt-0 items-center gap-4"
            label={
              <div className="flex flex-row gap-4 items-center">
                <HugeiconsIcon
                  icon={Mail02Icon}
                  size={20}
                  className={cx(
                    "shrink-0",
                    configuration.preferredSubmissionType ===
                      SubmissionType.SendByEmail
                      ? "text-primary-500"
                      : "text-alpha-100"
                  )}
                />
                <div>
                  <p className="text-sm font-medium text-content-1000 mb-1">
                    Send disclosure to applicant
                  </p>
                  <p className="text-content-300 text-xs">
                    Send a signing request via email to the applicant
                  </p>
                </div>
              </div>
            }
            checked={
              configuration.preferredSubmissionType ===
              SubmissionType.SendByEmail
            }
            onChange={() => {
              setConfiguration((prev) => ({
                ...prev,
                preferredSubmissionType: SubmissionType.SendByEmail
              }));
            }}
          />

          <Radio
            id="submission-type-in-person"
            name="submission-type"
            className="p-4 gap-4"
            checkboxClassName="my-auto"
            labelClassName="mt-0 items-center gap-4"
            label={
              <div className="flex flex-row gap-4 items-center">
                <HugeiconsIcon
                  icon={UserEdit01Icon}
                  size={20}
                  className={cx(
                    "shrink-0",
                    configuration.preferredSubmissionType ===
                      SubmissionType.InPerson
                      ? "text-primary-500"
                      : "text-alpha-100"
                  )}
                />
                <div>
                  <p className="text-sm font-medium text-content-1000 mb-1">
                    Sign here by applicant
                  </p>
                  <p className="text-content-300 text-xs">
                    Applicant will sign the disclosure here
                  </p>
                </div>
              </div>
            }
            checked={
              configuration.preferredSubmissionType === SubmissionType.InPerson
            }
            onChange={() => {
              setConfiguration((prev) => ({
                ...prev,
                preferredSubmissionType: SubmissionType.InPerson
              }));
            }}
          />
          <Show
            if={partner?.features.includes(
              FEATURES.MANUAL_APPLICATION_PROPERTY_MANAGER_SUBMISSION_DISCLOSURE
            )}
          >
            <Radio
              id="submission-type-sign-on-behalf"
              name="submission-type"
              className="p-4 gap-4"
              checkboxClassName="my-auto"
              labelClassName="mt-0 items-center gap-4"
              label={
                <div className="flex flex-row gap-4 items-center">
                  <HugeiconsIcon
                    icon={PropertyEditIcon}
                    size={20}
                    className={cx(
                      "shrink-0",
                      configuration.preferredSubmissionType ===
                        SubmissionType.SignOnBehalf
                        ? "text-primary-500"
                        : "text-alpha-100"
                    )}
                  />
                  <div>
                    <p className="text-sm font-medium text-content-1000 mb-1">
                      Sign on behalf of applicant
                    </p>
                    <p className="text-content-300 text-xs">
                      Property manager or authorized agent will sign the
                      disclosure here on behalf of applicant
                    </p>
                  </div>
                </div>
              }
              checked={
                configuration.preferredSubmissionType ===
                SubmissionType.SignOnBehalf
              }
              onChange={() => {
                setConfiguration((prev) => ({
                  ...prev,
                  preferredSubmissionType: SubmissionType.SignOnBehalf
                }));
              }}
            />
          </Show>
        </Card>

        <Show
          if={
            configuration.preferredSubmissionType === SubmissionType.SendByEmail
          }
        >
          <Card className="max-w-[500px] mx-auto p-5">
            <div className="space-y-4 animate-appear">
              <div className="space-y-2">
                <p className="text-sm font-medium text-content-1000">
                  Please add email
                </p>
                <p className="text-xs font-normal text-content-300">
                  Applicant will receive submission disclosure via email
                </p>
              </div>
              <Input
                id="applicant-email"
                className="w-[300px] !mb-0"
                placeholder="johndoe@example.com"
                hideOptional
                type="email"
                value={configuration.email || ""}
                onChange={(e) => {
                  setConfiguration((prev) => ({
                    ...prev,
                    email: e.target.value
                  }));
                }}
              />
            </div>
          </Card>
        </Show>
      </div>
      <div className="h-20 border-t border-alpha-100 bg-white gap-2 flex justify-end items-center pr-4">
        <Button
          tooltipZIndex={51}
          variant={ButtonVariant.PRIMARY}
          disabled={
            !configuration.preferredSubmissionType ||
            (configuration.preferredSubmissionType ===
              SubmissionType.SendByEmail &&
              !configuration.email)
          }
          className="w-[125px]"
          onClick={onNext}
          loading={isLoading}
        >
          Continue
        </Button>
      </div>
    </>
  );
};
